import React from 'react';
import { ModalService } from '@philips-internal/oswald-ui-components';
import { Breakpoint } from '@philips-internal/oswald-ui-constants';
import { AuthenticationService, MultiFactorAuthenticationRequiredError } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { getBreakpoint, isFunction, isUndefined } from '@philips-internal/oswald-ui-utils';
import { LoginDialog } from '../components/index.js';
import { MultiFactorAuthenticationModalService } from '../services/index.js';
const CREDENTIALS_PROMPT_MODAL = 'tws.login.crendentials-prompt-modal';
function getAvailableIds(arr) {
    return arr.map((v => v.id));
}
class FoundationContext {
    constructor() {
        this.fragments = {};
        this.modules = new Map();
        this.editEstablishment = true;
        this.editProfile = true;
        this.editSector = true;
        this.showEstablishment = true;
        this.showProfile = false;
        this.showSector = true;
        this.hideModuleSearch = false;
        this.moduleSearchExpression = 'tws.module-selector.search';
    }
    /**
     * Clears the information stored in the context.
     */
    clearInformation() {
        delete this.information;
        this.modules.clear();
    }
    /**
     * Returns all available establishments.
     */
    getAvailableEstablishments() {
        return this.information?.establishment?.available;
    }
    /**
     * Returns the current establishment.
     */
    getEstablishment() {
        return this.information?.establishment?.current;
    }
    /**
     * Returns whether the establishment can be edited.
     */
    canEditEstablishment() {
        return this.editEstablishment;
    }
    /**
     * Updates if the establishment can be edited.
     *
     * @param editEstablishment - The updated value.
     */
    setCanEditEstablishment(editEstablishment) {
        this.editEstablishment = editEstablishment;
    }
    /**
     * Returns all available modules.
     */
    getAvailableModules() {
        return this.information?.module.available;
    }
    /**
     * Returns a module by its id.
     *
     * @param id - The module id.
     */
    getModule(id) {
        if (!this.modules.has(id)) {
            return undefined;
        }
        return this.modules.get(id);
    }
    /**
     * Returns a named parameter of a given module.
     *
     * @param moduleId - The module id;
     * @param name - The parameter name.
     */
    getModuleParameter(moduleId, name) {
        const module = this.getModule(moduleId);
        if (!module) {
            throw Error(`[Foundation Context]: Module ${moduleId} not found.`);
        }
        const parameter = module.parameters.get(name);
        if (!parameter) {
            throw Error(`[Foundation Context]: Parameter ${name} not found.`);
        }
        return parameter;
    }
    /**
     * Returns all available profiles.
     */
    getAvailableProfiles() {
        return this.information?.profile?.available;
    }
    /**
     * Returns the current profile.
     */
    getProfile() {
        return this.information?.profile?.current;
    }
    /**
     * Returns whether the profile can be edited.
     */
    canEditProfile() {
        return this.editProfile;
    }
    /**
     * Updates if the profile can be edited.
     *
     * @param editProfile - The updated value.
     */
    setCanEditProfile(editProfile) {
        this.editProfile = editProfile;
    }
    /**
     * Returns all available sectors.
     */
    getAvailableSectors() {
        return this.information?.sector?.available;
    }
    /**
     * Returns the current sector.
     */
    getSector() {
        return this.information?.sector?.current;
    }
    /**
     * Returns whether the sector can be edited.
     */
    canEditSector() {
        return this.editSector;
    }
    /**
     * Updates if the sector can be edited.
     *
     * @param editSector - The updated value.
     */
    setCanEditSector(editSector) {
        this.editSector = editSector;
    }
    /**
     * Returns the current user.
     */
    getUser() {
        return this.information?.user;
    }
    /**
     * Returns whether the information is empty or not.
     * @returns
     */
    isEmpty() {
        return isUndefined(this.information);
    }
    /**
     * Returns the application menu fragment.
     */
    getMenuFragment() {
        return this.fragments.userMenu;
    }
    /**
     * Updates the menu fragment of the application.
     *
     * @param menuFragment - The updated fragment.
     */
    setMenuFragment(menuFragment) {
        this.fragments.userMenu = menuFragment;
    }
    /**
     * Returns the application root fragment.
     */
    getRootFragment() {
        return this.fragments.root;
    }
    /**
     * Updates the root fragment of the application.
     *
     * @param fragment - The updated fragment.
     */
    setRootFragment(fragment) {
        this.fragments.root = fragment;
    }
    /**
     * Updates the current establishment.
     *
     * @param establishment - The updated establishment.
     */
    setEstablishment(establishment) {
        if (!this.information) {
            throw Error('[Context]: Information is not filled.');
        }
        if (!this.information.establishment) {
            throw Error('[Context]: Establishment is not filled.');
        }
        const availableIds = getAvailableIds(this.getAvailableEstablishments() ?? []);
        if (!availableIds.includes(establishment.id)) {
            throw Error(`[Context]: Establishment with id "${establishment.id}" is not available.`);
        }
        this.information.establishment.current = establishment;
    }
    /**
     * Updates the context information once.
     *
     * @param information - The information being set.
     */
    setInformationOnce(information) {
        if (!this.isEmpty()) {
            throw Error('[Context]: Information already filled.');
        }
        this.information = information;
        for (const module of this.information.module.available) {
            this.modules.set(module.id, module);
        }
    }
    /**
     * Updates the current profile.
     *
     * @param profile - The updated profile.
     */
    setProfile(profile) {
        if (!this.information) {
            throw Error('[Context]: Information is not filled.');
        }
        if (!this.information.profile) {
            throw Error('[Context]: Profile is not filled.');
        }
        const availableIds = getAvailableIds(this.getAvailableProfiles() ?? []);
        if (!availableIds.includes(profile.id)) {
            throw Error(`[Context]: Profile with id "${profile.id}" is not available.`);
        }
        this.information.profile.current = profile;
    }
    /**
     * Updates the current sector.
     *
     * @param sector - The updated sector.
     */
    setSector(sector) {
        if (!this.information) {
            throw Error('[Context]: Information is not filled.');
        }
        if (!this.information.sector) {
            throw Error('[Context]: Sector is not filled.');
        }
        const availableIds = getAvailableIds(this.getAvailableSectors() ?? []);
        if (!availableIds.includes(sector.id)) {
            throw Error(`[Context]: Sector with id "${sector.id}" is not available.`);
        }
        this.information.sector.current = sector;
    }
    /**
     * Returns if the module search is hidden.
     */
    getHideModuleSearch() {
        return this.hideModuleSearch;
    }
    /**
     * Updates if the module search should be hidden.
     *
     * @param hideModuleSearch - The updated value.
     */
    setHideModuleSearch(hideModuleSearch) {
        this.hideModuleSearch = hideModuleSearch;
    }
    /**
     * Returns the expression used in the module search.
     */
    getModuleSearchExpression() {
        return this.moduleSearchExpression;
    }
    /**
     * Updates the expression used in the module search.
     *
     * @param moduleSearchExpression - The updated expression.
     */
    setModuleSearchExpression(moduleSearchExpression) {
        this.moduleSearchExpression = moduleSearchExpression;
    }
    /**
     * Prompts the user for credential validations.
     *
     * @param options - Prompt options.
     */
    promptCredentialsValidation(options = {}) {
        const onLoginClick = (data) => {
            const callMultiFactorAuthentication = (token) => {
                MultiFactorAuthenticationModalService.open({ storeToken: false, token })
                    .then((response) => onValidateSuccess({ ...response }))
                    .catch((error) => onValidateError(error));
            };
            function onValidateError(error) {
                if (error instanceof MultiFactorAuthenticationRequiredError) {
                    return callMultiFactorAuthentication(error.jwt);
                }
                if (!isFunction(options.onError)) {
                    return;
                }
                const abort = () => {
                    ModalService.close(CREDENTIALS_PROMPT_MODAL);
                };
                options.onError({ abort, error });
            }
            function onValidateSuccess(event) {
                ModalService.close(CREDENTIALS_PROMPT_MODAL);
                if (isFunction(options.onSuccess)) {
                    options.onSuccess(event);
                }
            }
            void AuthenticationService.validateCredentials(data)
                .then(onValidateSuccess)
                .catch(onValidateError);
        };
        const onCloseModal = () => {
            if (isFunction(options.onDismiss)) {
                options.onDismiss();
            }
        };
        const breakpoint = getBreakpoint();
        const isMobile = breakpoint === Breakpoint.SM || breakpoint === Breakpoint.XSM;
        const openFn = isMobile ? ModalService.openFullscreen : ModalService.openDefault;
        const modalOptions = {
            ...options.modalProperties,
            children: React.createElement(LoginDialog, { loginExpression: options.validateButtonExpression, onLogin: onLoginClick }),
            closeable: Boolean(options.onDismiss),
            focusOnOpen: false,
            id: CREDENTIALS_PROMPT_MODAL,
            onClose: () => onCloseModal(),
            title: 'tws.login.revalidation',
        };
        openFn(modalOptions);
    }
    /**
     * Returns if the establishment is shown in the Spotlight.
     */
    canShowEstablishment() {
        return this.showEstablishment;
    }
    /**
     * Updates if the establishment is shown in the Spotlight.
     *
     * @param showEstablishment - The updated value.
     */
    setShowEstablishment(showEstablishment) {
        this.showEstablishment = showEstablishment;
    }
    /**
     * Returns if the profile is shown in the Spotlight.
     */
    canShowProfile() {
        return this.showProfile;
    }
    /**
     * Updates if the profile is shown in the Spotlight.
     *
     * @param showProfile - The updated value.
     */
    setShowProfile(showProfile) {
        this.showProfile = showProfile;
    }
    /**
     * Returns if the sector is shown in the Spotlight.
     */
    canShowSector() {
        return this.showSector;
    }
    /**
     * Updates if the sector is shown in the Spotight.
     *
     * @param showSector - The updated value.
     */
    setShowSector(showSector) {
        this.showSector = showSector;
    }
}
const foundationContext = new FoundationContext();
export { foundationContext };
//# sourceMappingURL=foundation-context.js.map